import * as Dialog from '@radix-ui/react-dialog';
import { CornerDownLeft, Search } from 'lucide-react';
import { type ReactNode, useEffect, useId, useRef, useState } from 'react';

/**
 * One row of a palette: what it is, where it sits, and what choosing it does.
 * The caller filters and orders the rows; the palette shows them in that order,
 * under their group's heading where the group changes.
 */
export type PaletteItem = {
  id: string;
  group: string;
  title: string;
  detail?: string;
  icon: ReactNode;
  run: () => void;
};

/**
 * A palette: a search field over a list of destinations and commands, the
 * desktop app's "Go to…" (⌘P), after Querybara's quick open. Radix Dialog gives
 * the dialog role, the focus trap and Escape. The field keeps the focus the
 * whole time; the arrow keys move one highlight that the pointer shares, and
 * Return runs it. Unlike `Modal` a click outside closes it, because a palette
 * holds no draft. The field and rows are its own controls, so they live in
 * the primitive module rather than in the page that uses it.
 */
export function Palette({
  open,
  onClose,
  label,
  placeholder,
  query,
  onQuery,
  items,
  empty,
}: {
  open: boolean;
  onClose: () => void;
  label: string;
  placeholder: string;
  query: string;
  onQuery: (query: string) => void;
  items: PaletteItem[];
  empty: ReactNode;
}) {
  const [active, setActive] = useState(0);
  const list = useId();
  const rows = useRef(new Map<number, HTMLElement>());

  useEffect(() => {
    rows.current.get(active)?.scrollIntoView({ block: 'nearest' });
  }, [active]);

  const choose = (index: number) => {
    const item = items[index];

    if (item) {
      onClose();
      item.run();
    }
  };

  return (
    <Dialog.Root open={open} onOpenChange={next => !next && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="palette-backdrop">
          <Dialog.Content className="palette" aria-describedby={undefined}>
            <Dialog.Title className="sr-only">{label}</Dialog.Title>
            <div className="palette-field">
              <Search aria-hidden="true" />
              <input
                // biome-ignore lint/a11y/noAutofocus: the palette exists to be typed into; Radix would focus it anyway
                autoFocus
                value={query}
                placeholder={placeholder}
                aria-label={label}
                role="combobox"
                aria-expanded={items.length > 0}
                aria-controls={list}
                aria-activedescendant={items[active] ? `${list}-${active}` : undefined}
                autoComplete="off"
                spellCheck={false}
                onChange={event => {
                  // A new query starts at the top of what it found.
                  setActive(0);
                  onQuery(event.target.value);
                }}
                onKeyDown={event => {
                  if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
                    event.preventDefault();

                    if (items.length) {
                      const step = event.key === 'ArrowDown' ? 1 : -1;

                      setActive(current => (current + step + items.length) % items.length);
                    }
                  } else if (event.key === 'Enter') {
                    event.preventDefault();
                    choose(active);
                  }
                }}
              />
              <kbd>esc</kbd>
            </div>
            {items.length ? (
              <div className="palette-list" id={list} role="listbox" aria-label={label}>
                {items.map((item, index) => (
                  <div key={item.id}>
                    {(index === 0 || items[index - 1].group !== item.group) && (
                      <div className="palette-group" aria-hidden="true">
                        {item.group}
                      </div>
                    )}
                    <div
                      id={`${list}-${index}`}
                      ref={element => {
                        if (element) {
                          rows.current.set(index, element);
                        } else {
                          rows.current.delete(index);
                        }
                      }}
                      role="option"
                      tabIndex={-1}
                      aria-selected={index === active}
                      className="palette-row"
                      onPointerMove={() => setActive(index)}
                      onClick={() => choose(index)}
                      onKeyDown={event => event.key === 'Enter' && choose(index)}
                    >
                      {item.icon}
                      <span className="palette-title">{item.title}</span>
                      {item.detail && <span className="palette-detail">{item.detail}</span>}
                      {index === active && <CornerDownLeft aria-hidden="true" />}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="palette-empty" role="status">
                {empty}
              </p>
            )}
            <footer className="palette-hints" aria-hidden="true">
              <span>
                <kbd>↑</kbd>
                <kbd>↓</kbd> move
              </span>
              <span>
                <kbd>↩</kbd> open
              </span>
              <span>
                <kbd>esc</kbd> close
              </span>
            </footer>
          </Dialog.Content>
        </Dialog.Overlay>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
